import React from 'react';
import { Search, Compass, Layers, Rocket } from 'lucide-react';

const ProcessSection: React.FC = () => {
  const steps = [
    { 
      num: '01', 
      icon: <Search className="w-6 h-6 text-[#C5A059]" />,
      title: 'Assess', 
      desc: 'Investor readiness audit, credibility gaps, and perception analysis to identify your baseline positioning.' 
    },
    { 
      num: '02', 
      icon: <Compass className="w-6 h-6 text-[#C5A059]" />,
      title: 'Position', 
      desc: 'Defining your unique commercial story, ESG narrative, and competitive advantage for global markets.' 
    },
    { 
      num: '03', 
      icon: <Layers className="w-6 h-6 text-[#C5A059]" />,
      title: 'Package', 
      desc: 'Building institutional-grade investor documents, buyer materials, and a high-trust digital presence.' 
    },
    { 
      num: '04', 
      icon: <Rocket className="w-6 h-6 text-[#C5A059]" />,
      title: 'Activate', 
      desc: 'Deploying strategic visibility systems and supporting market entry to connect with capital and buyers.' 
    }
  ];

  return (
    <section id="process" className="py-32 bg-[#010712] flex justify-center px-4 scroll-mt-32">
      <div className="w-full max-w-[1440px] px-4 md:px-8 flex flex-col gap-16">
        
        {/* Narrative Header - Level 1 label */}
        <div className="max-w-4xl">
          <span className="text-zinc-400 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.3em] mb-6 block">
            Tactical Roadmap
          </span>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight text-white mb-8">
            Our Mine-to-Market Framework.
          </h2>
          <p className="text-zinc-400 text-base md:text-lg leading-relaxed">
            We don’t improvise. We run a proven commercial positioning system designed to ensure your mine looks as serious as it actually is, bridging the gap between operational reality and institutional perception.
          </p>
        </div>

        {/* Grid of Steps - Glass Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {steps.map((step, i) => (
            <div 
              key={i} 
              className="p-10 bg-[#0a1220]/50 hover:bg-[#0a1220]/80 transition-all group relative overflow-hidden rounded-none"
            >
              <div className="absolute top-6 right-6 text-xl font-bold tracking-widest text-zinc-700/60 group-hover:text-[#C5A059]/20 transition-colors">
                {step.num}
              </div>
              <div className="w-12 h-12 flex items-center justify-center bg-[#111d30] rounded-none mb-6 transition-all group-hover:bg-[#1b2b42]">
                {step.icon}
              </div>
              <h4 className="font-bold text-[15px] uppercase tracking-widest mb-3 text-white">
                {step.title}
              </h4>
              <p className="text-sm text-zinc-400 leading-relaxed max-w-md">
                {step.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Glass Audit Callout */}
        <div className="mt-8 p-8 md:p-12 bg-[#0a1220]/40 backdrop-blur-xl text-white rounded-none flex flex-col md:flex-row justify-between items-center gap-10 relative overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.5)]">
          <div className="absolute inset-0 bg-gradient-to-r from-[#C5A059]/5 to-transparent pointer-events-none" />
          <div className="max-w-xl text-center md:text-left relative z-10">
            <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-4">
              Not Sure Where Your Mine Stands?
            </h3>
            <p className="text-zinc-300 text-base md:text-lg leading-relaxed opacity-90">
              Obtain a comprehensive credibility scorecard and a clear strategic roadmap through our Mine Visibility & Investor Readiness Audit.
            </p>
          </div>
          <a 
            href="https://wa.me/27839287731"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#C5A059] text-zinc-950 px-6 md:px-10 py-4 md:py-5 rounded-none font-black text-[11px] md:text-[13px] uppercase tracking-[0.1em] md:tracking-[0.2em] hover:bg-[#b08c49] transition-all whitespace-nowrap border-none shadow-none relative z-10"
          >
            Request Audit
          </a>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
